import type { Point, RuntimeLevel, RuntimeObjective } from '../types';
import { type Palette, PALETTES, drawTile, drawBackground, isOverlayChar, drawFloorAt } from './TileDrawing';
import { getAssets } from './AssetLoader';

export type { Palette };
export { PALETTES };

interface RenderState {
  level: RuntimeLevel;
  player: Point;
  playerFacing: Point;
  currentTarget: Point | null;
  objectives: RuntimeObjective[];
  phase: 'objectives' | 'exit' | 'complete';
  activeObjectiveIndex: number;
  holdProgress: number;
  elapsedSeconds: number;
}

interface Viewport {
  camera: Point;
  origin: Point;
  tileSize: number;
}

export class PixelRenderer {
  private readonly canvas: HTMLCanvasElement;
  private readonly ctx: CanvasRenderingContext2D;
  private readonly worldTileSize = 8;
  private readonly virtualWidth = 384;
  private readonly virtualHeight = 216;

  constructor(canvas: HTMLCanvasElement) {
    const context = canvas.getContext('2d', { alpha: false });
    if (!context) {
      throw new Error('Unable to get 2D context.');
    }

    this.canvas = canvas;
    this.ctx = context;
    this.canvas.width = this.virtualWidth;
    this.canvas.height = this.virtualHeight;
    this.ctx.imageSmoothingEnabled = false;
    this.ctx.textBaseline = 'top';
  }

  public resize(): void {
    this.ctx.imageSmoothingEnabled = false;
  }

  public render(state: RenderState): void {
    const palette = PALETTES[state.level.theme];
    this.drawBackground(palette, state.elapsedSeconds);

    const view = {
          camera: this.computeFollowCamera(state.level.width, state.level.height, state.player),
          origin: { x: 0, y: 0 },
          tileSize: this.worldTileSize,
        };

    this.drawWorld(state, palette, view);
    this.drawTargets(state, palette, view);
    this.drawPlayer(state, view);
    this.drawGuidance(state, palette, view);
  }

  private drawBackground(palette: Palette, elapsed: number): void {
    drawBackground(this.ctx, palette, elapsed, this.virtualWidth, this.virtualHeight);
  }

  private computeFollowCamera(levelWidth: number, levelHeight: number, player: Point): Point {
    const visibleTilesX = this.virtualWidth / this.worldTileSize;
    const visibleTilesY = this.virtualHeight / this.worldTileSize;
    const halfX = visibleTilesX * 0.5;
    const halfY = visibleTilesY * 0.5;

    return {
      x: this.clamp(player.x - halfX, 0, Math.max(0, levelWidth - visibleTilesX)),
      y: this.clamp(player.y - halfY, 0, Math.max(0, levelHeight - visibleTilesY)),
    };
  }

  private drawWorld(state: RenderState, palette: Palette, view: Viewport): void {
    const level = state.level;
    const xStart = Math.max(0, Math.floor(view.camera.x) - 1);
    const yStart = Math.max(0, Math.floor(view.camera.y) - 1);
    const xEnd = Math.min(level.width, Math.ceil(view.camera.x + this.virtualWidth / view.tileSize) + 1);
    const yEnd = Math.min(level.height, Math.ceil(view.camera.y + this.virtualHeight / view.tileSize) + 1);

    // Pass 1: draw all tiles (floors, walls, furniture, non-overlays)
    //         and draw floor background for overlay-type tiles
    for (let y = yStart; y < yEnd; y += 1) {
      for (let x = xStart; x < xEnd; x += 1) {
        const tile = level.tiles[y][x];
        const px = Math.floor(view.origin.x + (x - view.camera.x) * view.tileSize);
        const py = Math.floor(view.origin.y + (y - view.camera.y) * view.tileSize);
        if (isOverlayChar(tile)) {
          // Draw floor underneath overlay decorations
          drawFloorAt(this.ctx, x, y, px, py, view.tileSize, palette);
        } else {
          const rot = level.rotations[y]?.[x] ?? 0;
          drawTile(this.ctx, tile, x, y, px, py, view.tileSize, palette, state.elapsedSeconds, rot);
        }
      }
    }

    // Pass 2: draw overlay decoration tiles on top of the floor
    for (let y = yStart; y < yEnd; y += 1) {
      for (let x = xStart; x < xEnd; x += 1) {
        const tile = level.tiles[y][x];
        if (!isOverlayChar(tile)) continue;
        const px = Math.floor(view.origin.x + (x - view.camera.x) * view.tileSize);
        const py = Math.floor(view.origin.y + (y - view.camera.y) * view.tileSize);
        const rot = level.rotations[y]?.[x] ?? 0;
        drawTile(this.ctx, tile, x, y, px, py, view.tileSize, palette, state.elapsedSeconds, rot);
      }
    }
  }

  private drawTargets(state: RenderState, palette: Palette, view: Viewport): void {
    const pulse = 0.55 + Math.sin(state.elapsedSeconds * 4.4) * 0.45;

    for (let i = 0; i < state.objectives.length; i += 1) {
      const objective = state.objectives[i];
      const screen = this.worldToScreen(objective.position, view);
      const isActive = state.phase === 'objectives' && i === state.activeObjectiveIndex;
      const color = objective.activated ? '#7f8fb3' : isActive ? palette.accent : '#ccb5ff';

      const size = objective.activated ? 4 : 6 + Math.floor(pulse * 2);
      this.ctx.fillStyle = color;
      this.ctx.fillRect(screen.x - size / 2, screen.y - size / 2, size, size);

      if (isActive) {
        this.ctx.strokeStyle = palette.accentSoft;
        this.ctx.lineWidth = 1;
        this.ctx.strokeRect(screen.x - 7, screen.y - 7, 14, 14);
      }
    }

    if (state.phase === 'exit' || state.phase === 'complete') {
      const screen = this.worldToScreen(state.level.exit, view);
      const size = 8 + Math.floor(pulse * 3);
      this.ctx.fillStyle = state.phase === 'complete' ? '#9fb8ff' : '#ffd6b4';
      this.ctx.fillRect(screen.x - size / 2, screen.y - size / 2, size, size);
      this.ctx.strokeStyle = '#fff3e5';
      this.ctx.strokeRect(screen.x - 8, screen.y - 8, 16, 16);
    }
  }

  private drawPlayer(state: RenderState, view: Viewport): void {
    const screen = this.worldToScreen(state.player, view);
    const assets = getAssets();

    if (assets) {
      // Use player spritesheet — row 0 = character 1, 4 frames
      // Pick frame based on facing direction
      const frame = state.playerFacing.x < 0 ? 1 : 0;
      const spriteSize = view.tileSize * 1.5; // Player slightly larger than a tile
      const dx = screen.x - spriteSize / 2;
      const dy = screen.y - spriteSize / 2 - 1;
      // Shadow
      this.ctx.fillStyle = 'rgba(0,0,0,0.25)';
      this.ctx.fillRect(screen.x - 3, screen.y + view.tileSize / 2 - 1, 6, 2);

      // If facing left, flip the sprite
      if (state.playerFacing.x < 0) {
        this.ctx.save();
        this.ctx.translate(screen.x, 0);
        this.ctx.scale(-1, 1);
        assets.players.draw(this.ctx, frame, -spriteSize / 2, dy, spriteSize, spriteSize);
        this.ctx.restore();
      } else {
        assets.players.draw(this.ctx, frame, dx, dy, spriteSize, spriteSize);
      }
    } else {
      // Fallback: simple colored rectangles
      this.ctx.fillStyle = 'rgba(0,0,0,0.35)';
      this.ctx.fillRect(screen.x - 3, screen.y + 4, 6, 2);

      this.ctx.fillStyle = '#ffe8f0';
      this.ctx.fillRect(screen.x - 3, screen.y - 5, 6, 10);
      this.ctx.fillStyle = '#f8a9cd';
      this.ctx.fillRect(screen.x - 2, screen.y - 5, 4, 2);
      this.ctx.fillStyle = '#314571';
      this.ctx.fillRect(screen.x - 2, screen.y + 1, 4, 3);

      const eyeX = state.playerFacing.x >= 0 ? 1 : -2;
      this.ctx.fillStyle = '#1c2038';
      this.ctx.fillRect(screen.x + eyeX, screen.y - 2, 1, 1);
    }
  }

  private drawGuidance(state: RenderState, palette: Palette, view: Viewport): void {
    if (!state.currentTarget) {
      return;
    }

    const player = this.worldToScreen(state.player, view);
    const target = this.worldToScreen(state.currentTarget, view);
    const dx = target.x - player.x;
    const dy = target.y - player.y;
    const distanceSq = dx * dx + dy * dy;

    if (distanceSq < 400) {
      this.drawHoldBar(state, palette);
      return;
    }

    const angle = Math.atan2(dy, dx);
    const arrowX = player.x + Math.cos(angle) * 14;
    const arrowY = player.y + Math.sin(angle) * 14;

    this.ctx.fillStyle = palette.accent;
    this.ctx.beginPath();
    this.ctx.moveTo(arrowX + Math.cos(angle) * 4, arrowY + Math.sin(angle) * 4);
    this.ctx.lineTo(arrowX + Math.cos(angle + 2.3) * 3, arrowY + Math.sin(angle + 2.3) * 3);
    this.ctx.lineTo(arrowX + Math.cos(angle - 2.3) * 3, arrowY + Math.sin(angle - 2.3) * 3);
    this.ctx.closePath();
    this.ctx.fill();

    this.drawHoldBar(state, palette);
  }

  private drawHoldBar(state: RenderState, palette: Palette): void {
    if (state.holdProgress <= 0) {
      return;
    }

    const width = 74;
    const height = 8;
    const x = Math.floor(this.virtualWidth * 0.5 - width * 0.5);
    const y = this.virtualHeight - 30;
    this.ctx.fillStyle = 'rgba(12,16,34,0.75)';
    this.ctx.fillRect(x, y, width, height);
    this.ctx.fillStyle = palette.accent;
    this.ctx.fillRect(x + 1, y + 1, Math.floor((width - 2) * state.holdProgress), height - 2);
    this.ctx.strokeStyle = '#f5f7ff';
    this.ctx.strokeRect(x, y, width, height);
  }

  private worldToScreen(point: Point, view: Viewport): Point {
    return {
      x: Math.floor(view.origin.x + (point.x - view.camera.x) * view.tileSize),
      y: Math.floor(view.origin.y + (point.y - view.camera.y) * view.tileSize),
    };
  }

  private clamp(value: number, min: number, max: number): number {
    return Math.max(min, Math.min(max, value));
  }

}
